import { decide, held } from "./model";
import { siteToolsNow } from "../site/webmcp";
import { PAGES, sitePages } from "../site/siteMap";
import { askLog } from "../components/estateVoice";

/**
 * What to DO about something somebody said.
 * ---------------------------------------------------------------------------
 * The ask used to be a keyword resolver: a verb, a page name, and a table of
 * synonyms. That works for "go to the forge wiki" and fails for everything
 * said the way people actually say it - "what's broken on my windows boxes",
 * "which apps can I move".
 *
 * So the model decides instead, and what it decides BETWEEN is the three tools
 * this app already publishes to WebMCP. That is the point of this file: the
 * butler is not a second way of driving the product with its own idea of what
 * the pages are. It reaches for the same `navigate_to_page` an agent in the
 * browser would, gets the same answer, and leaves the page in the same state.
 * One set of doors, two things that can open them.
 *
 * The decision is CONSTRAINED rather than parsed hopefully. WebLLM can hold a
 * model to a JSON schema while it generates, so `page` is an enum of the ids
 * that exist and `tool` an enum of the tools that exist: a half-billion
 * parameter model cannot answer with a page this product does not have, which
 * is the failure mode that would otherwise make this useless.
 *
 * And it is allowed to say no. A question that is not about going anywhere
 * comes back as `answer`, and the caller does what it did before - puts the
 * words in the page's search.
 *
 * TWO DECISIONS, NOT ONE
 * ----------------------
 * Going somewhere is no longer the only thing that can be asked for: a
 * dashboard can be built and the whole product can be repainted, and those are
 * not pages. The obvious way to let the model reach them - give it the list of
 * tools and let it pick - is the thing already known not to work here (see the
 * note on the schema below).
 *
 * So the choice is split in two, and each half is a choice the model can
 * actually make:
 *
 *   1. WHAT KIND of thing is this - looking at something, building a
 *      dashboard, or changing the colours? Three plain English alternatives
 *      with an example apiece, which a half-billion-parameter model gets right
 *      far more often than three function names.
 *   2. The details FOR THAT KIND, under a schema shaped for it alone: a page
 *      and its filters, or a name and a subject, or a list of colours.
 *
 * Then the matching tool is called - `navigate_to_page`, `create_dashboard`,
 * `set_palette` - with arguments the model filled in. The tools are still the
 * only doors; what changed is that there are now three corridors to them.
 *
 * Every answer is still GROUNDED before it is acted on: a colour the sentence
 * never mentions is dropped, an intent whose words are nowhere in the sentence
 * is not believed. The model is good at understanding and bad at restraint,
 * and the restraint is cheap to supply here.
 * ---------------------------------------------------------------------------
 */

/* The shape an answer has to take. The enums are the whole trick: `page` can
   only be a page this installation has, so the model cannot invent one.

   It is deliberately NOT "choose a tool". Asked to pick between four names a
   half-billion-parameter model picks the one with the fewest required fields
   every time - it answered `list_pages` to "what's broken on my windows boxes"
   while filling in severity, lens and search perfectly. Two decisions it can
   make (is this a journey, and which page) beat one it cannot (which of four
   tools), and the journey is still carried out by `navigate_to_page`: the same
   tool, through the same door, just not chosen by name. */
function schemaFor(pages) {
  return {
    type: "object",
    properties: {
      page: { type: "string", enum: pages.map(p => p.id) },
      view: { type: "string", enum: ["table", "cards"] },
      /* the filters the address really carries, named as themselves so the
         model has somewhere to put "critical" and "vulnerable" */
      severity: { type: "string", enum: ["critical", "high", "medium", "low", "unknown"] },
      lens: { type: "string", enum: ["vulnerable", "documented", "documentable"] },
      search: { type: "string" },
    },
    required: ["page"],
  };
}

/* What the model is told, and it is SHORT on purpose.
   -------------------------------------------------------------------------
   Two things were learnt the hard way here, both of them about how little room
   a half-billion-parameter model has:

   Do not ask it to choose a tool. Given four names it answered `list_pages`
   to everything, while filling in severity, lens and search perfectly - the
   understanding was there, the choice was not.

   Do not ask it a yes-or-no either. Asked `"go": true or false` on top of a
   page, it said false to every sentence, including "take me home". A boolean
   at the end of a thousand-token brief is a coin.

   So it is asked for exactly one thing it can be held to: which page. The
   enum in the schema means it cannot name one that does not exist, the filters
   are optional extras it fills in well, and the decision of whether to go at
   all is not its to make - the keyword resolver has already taken the explicit
   cases, and for the rest the nearest page beats searching a page for a
   sentence that matches nothing.

   The page list is one short clause each for the same reason. */
function brief(pages) {
  const short = (what) => String(what || "").split(/[.:]/)[0].split(/\s+/).slice(0, 9).join(" ");
  return [
    "Which page of AppFactor answers this? Reply with JSON.",
    'Add "severity" if they name one, "lens" for vulnerable/documented/documentable, "search" for a name to look for.',
    "",
    ...pages.map(p => `${p.id}: ${short(p.what)}`),
  ].join("\n");
}

/* What has to be in the sentence for a filter to be taken seriously. */
const LENS_WORDS = {
  vulnerable: ["vulnerab", "cve", "to fix", "broken", "exposed", "risk"],
  documented: ["document", "docs", "wiki", "written", "readme"],
  documentable: ["documentable", "can document", "repositor", "repo"],
};

/**
 * Turn the model's answer into `navigate_to_page`'s own arguments - keeping
 * only the parts that were actually asked for.
 *
 * A schema with optional fields in it is a schema a small model fills in. Ask
 * it where to go and it will hand back a severity and a lens as well, every
 * time, whether or not either was mentioned: "take me home" came back as the
 * estate with `severity: low` and `search: scan`, and went somewhere nobody
 * asked for with two filters nobody named.
 *
 * So a filter survives only if the word for it is in what was said. The model
 * is good at picking the page; it is guessing at the rest, and a guess that
 * changes what you are looking at is worse than no guess.
 */
function argsFrom(choice, said) {
  const words = String(said || "").toLowerCase();
  const filters = {};

  if (choice.severity && words.includes(choice.severity)) filters.severity = choice.severity;
  if (choice.lens && (LENS_WORDS[choice.lens] || []).some(w => words.includes(w))) filters.lens = choice.lens;

  /* ...and the same for a search: it has to be something that was said */
  const asked = String(choice.search || "").trim();
  const search = asked && words.includes(asked.toLowerCase()) ? asked : "";

  /* a view nobody named is the page's own */
  const view = choice.view && words.includes(choice.view) ? choice.view : "";

  return {
    page: choice.page,
    ...(view ? { view } : {}),
    ...(search ? { search } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
  };
}

/* ------------------------------------------------------------ what kind */

/**
 * The three things that can be asked for, described as what they are rather
 * than as the name of the function that does them.
 *
 * `say` is the example. A model this size reasons by resemblance far more than
 * by definition, so one sentence that looks like the request is worth more
 * than a paragraph explaining the category.
 */
const KINDS = [
  {
    id: "look",
    what: "look at something that already exists, or go to a page",
    say: "show me the applications with critical vulnerabilities",
    /* nothing is required: this is where everything lands that is not plainly
       one of the other two, and it changes nothing that going somewhere else
       does not change back */
    verbs: null,
    nouns: null,
  },
  {
    id: "build",
    what: "build a new dashboard page out of widgets",
    say: "create a new dashboard page with the forge stats on it",
    verbs: ["create", "make", "build", "add", "new", "set up", "put together"],
    nouns: ["dashboard", "board", "widget"],
  },
  {
    id: "paint",
    what: "change the colours or the palette the pages are drawn in",
    say: "change the palette to four magenta colours and one orange",
    verbs: ["change", "make", "set", "use", "paint", "switch", "apply", "turn", "recolour", "recolor", "want"],
    nouns: ["colour", "color", "palette", "pallette", "pallet", "theme", "tint"],
  },
];

const KIND_SCHEMA = {
  type: "object",
  properties: { kind: { type: "string", enum: KINDS.map(k => k.id) } },
  required: ["kind"],
};

const KIND_BRIEF = [
  "What is being asked for? Reply with JSON.",
  "",
  ...KINDS.map(k => `${k.id}: ${k.what}. For instance: "${k.say}"`),
].join("\n");

/** The details, once the kind is known. */
const BUILD_SCHEMA = {
  type: "object",
  properties: {
    name: { type: "string", description: "a short name for the new dashboard" },
    about: { type: "string", description: "the subjects it should cover, as words" },
  },
  required: ["about"],
};

const BUILD_BRIEF = [
  "A new dashboard is being asked for. Reply with JSON.",
  '"about" is the subjects it should show, copied from what was said - for instance',
  '"forge stats, assessments, vulnerabilities". "name" is a short title for the page.',
].join("\n");

const PAINT_SCHEMA = {
  type: "object",
  properties: {
    colors: {
      type: "array",
      items: { type: "string" },
      description: 'the colours asked for, each with its count: ["4 magenta", "1 orange"]',
    },
  },
  required: ["colors"],
};

const PAINT_BRIEF = [
  "Colours are being asked for. Reply with JSON.",
  'List every colour named, each with how many of it was asked for.',
  '"four magenta colours and one orange" is ["4 magenta", "1 orange"].',
  '"make everything blue" is ["1 blue"].',
].join("\n");

/**
 * Which kind this is, from the sentence and the model's vote together.
 *
 * The vote alone is not enough. Shown three alternatives a model this size
 * will answer with one of them whatever it was asked - "create a new dashboard
 * page that will contain all the forge stats" came back as `paint`, which
 * would have repainted the entire product in reply to a request for a page.
 *
 * So the sentence decides WHICH DOORS ARE OPEN and the model decides only when
 * more than one of them is. A door opens on a verb and a noun together, which
 * is what separates "show me the dashboard" from "create a dashboard": both
 * name a board, only one of them asks for a new one.
 *
 * What is left for the model is what it is actually good at - which page, which
 * subjects, which colours, out of a sentence nobody wrote for a parser. The
 * choice it could not make reliably is simply not one it is asked to make
 * alone.
 */
function kindOf(said, voted) {
  const words = ` ${String(said).toLowerCase()} `;
  const has = (list) => list.some(w => words.includes(w));

  const open = KINDS.filter(k => k.verbs && has(k.verbs) && has(k.nouns));
  if (!open.length) return "look";
  if (open.length === 1) return open[0].id;
  // both doors are open - which is when the model's reading is worth having
  return open.some(k => k.id === voted) ? voted : open[0].id;
}

/** A tool by name, or nothing. */
const toolNamed = (tools, name) => tools.find(t => t.name === name) || null;

/** What a tool said, flattened to the one line the log wants. */
const spoke = (result) => String(result?.content?.[0]?.text || "").replace(/\s+/g, " ");

/** Is there a model up to do this at all? */
export const canThink = () => !!held();

/**
 * Think about what was said, and do it.
 *
 * @returns { did, tool, said } where `did` is false if nothing was done - in
 *          which case the caller should fall back to whatever it did before.
 */
export async function act(what, { env } = {}) {
  const asked = String(what || "").trim();
  if (!asked || !held()) return { did: false };

  const tools = siteToolsNow();
  if (!tools.length) return { did: false };

  /* ---- first: what kind of thing is this ---- */
  let kind = "look";
  try {
    const picked = await decide(
      [{ role: "system", content: KIND_BRIEF }, { role: "user", content: asked }],
      KIND_SCHEMA,
    );
    kind = kindOf(asked, picked?.kind);
    askLog("the butler took that as a", kind, picked?.kind !== kind ? `(it voted ${picked?.kind})` : "");
  } catch (e) {
    askLog("the butler could not think:", e?.message || e);
    return { did: false };
  }

  if (kind === "build") return build(asked, tools);
  if (kind === "paint") return paint(asked, tools);
  return look(asked, tools, env);
}

/* ------------------------------------------------------------ the three */

/** Go and see something. */
async function look(asked, tools, env) {
  /* only the pages this installation has, which is also what the tools will
     accept - the schema and the door agree */
  const pages = env ? sitePages(env) : PAGES;

  let choice = null;
  try {
    choice = await decide(
      [
        { role: "system", content: brief(pages) },
        { role: "user", content: asked },
      ],
      schemaFor(pages),
    );
  } catch (e) {
    askLog("the butler could not think:", e?.message || e);
    return { did: false };
  }

  if (!choice) { askLog("the butler had no answer"); return { did: false }; }
  askLog("the butler read that as", choice);

  if (!choice.page) { askLog("...but named no page, so the words go to the page's own search"); return { did: false }; }

  const tool = toolNamed(tools, "navigate_to_page");
  if (!tool) return { did: false };

  try {
    const said = spoke(await tool.execute(argsFrom(choice, asked)));
    askLog("navigate_to_page ·", said.slice(0, 150));
    return { did: true, tool: "navigate_to_page", said };
  } catch (e) {
    askLog("the tool refused:", e?.message || e);
    return { did: false };
  }
}

/**
 * Build a dashboard.
 *
 * The widgets are not chosen here and they are not chosen by the model either:
 * `create_dashboard` matches the subjects against its own catalogue, which is
 * the only place that knows what widgets exist. All the model has to produce
 * is the subjects, and if it produces nothing usable the sentence itself is
 * handed over - "all the forge stats, assessment and vulnerability statuses"
 * is already a perfectly good description of what to put on the page.
 */
async function build(asked, tools) {
  const tool = toolNamed(tools, "create_dashboard");
  if (!tool) return { did: false };

  let plan = null;
  try {
    plan = await decide(
      [{ role: "system", content: BUILD_BRIEF }, { role: "user", content: asked }],
      BUILD_SCHEMA,
    );
  } catch (e) { /* the sentence will do */ }
  askLog("the butler would build", plan || "(from the sentence itself)");

  const about = String(plan?.about || "").trim() || asked;
  const name = String(plan?.name || "").trim();

  try {
    const said = spoke(await tool.execute({ about, ...(name ? { name } : {}) }));
    askLog("create_dashboard ·", said.slice(0, 200));
    return { did: true, tool: "create_dashboard", said };
  } catch (e) {
    askLog("the tool refused:", e?.message || e);
    return { did: false };
  }
}

/**
 * Repaint.
 *
 * Grounded twice over: a colour the model named has to be a word in what was
 * actually said, and if none of them survive that the sentence is handed to
 * the tool, which can read colour names out of plain text itself. Between the
 * two there is no way to end up painting the product a colour nobody asked
 * for, which matters more here than anywhere else - it is the one action that
 * changes every page at once.
 */
async function paint(asked, tools) {
  const tool = toolNamed(tools, "set_palette");
  if (!tool) return { did: false };

  let wish = null;
  try {
    wish = await decide(
      [{ role: "system", content: PAINT_BRIEF }, { role: "user", content: asked }],
      PAINT_SCHEMA,
    );
  } catch (e) { /* the sentence will do */ }
  askLog("the butler read the colours as", wish?.colors || "(nothing - using the sentence)");

  const words = asked.toLowerCase();
  /* the colour's own word has to be in the sentence - the stem of it, so that
     "magentas" and "magenta" and "a magenta one" all count as having said it */
  const kept = (wish?.colors || [])
    .map(c => String(c).trim())
    .filter(c => {
      const word = c.replace(/[^a-z]/gi, "").toLowerCase();
      return word.length > 2 && words.includes(word.slice(0, 4));
    });

  /* one page, or the lot - and the lot is the default, because somebody who
     says "the palette" without naming a page means the product */
  const scope = /\b(this page|here|only this)\b/.test(words) ? undefined : "everywhere";

  try {
    const args = kept.length ? { colors: kept } : { colors: [asked] };
    const said = spoke(await tool.execute({ ...args, ...(scope ? { scope } : {}) }));
    askLog("set_palette ·", said.slice(0, 200));
    return { did: true, tool: "set_palette", said };
  } catch (e) {
    askLog("the tool refused:", e?.message || e);
    return { did: false };
  }
}

export default act;
